import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import ReactTable from 'react-table';
import qs from 'qs';
import _ from 'lodash';
import { reset } from 'redux-form';

import {
  DATE_FORMAT_FOR_UI,
  DATE_FORMAT_FOR_UI_DASHED,
  DATE_FORMAT_DAY_MONTH_NUMBER,
  NOTIFICATION_DIALOG_MESSAGES,
} from 'Common/Constants';
import {
  ProductionRecordFieldEnum as ProductionRecordFields,
  ProductionGridFieldEnum as ProductionGridFields,
  EditProductionRecordFieldEnum as EditProductionRecordFields,
} from 'Common/FieldEnum';
import { TitledCard } from 'Components';
import { caseInsensitiveFilter } from 'Common/GridHelper';
import * as CommonStateSelectors from 'State/selectors';
import { ProductionStatusEnum } from 'Common/Constants/Enumeration/ProductionStatusEnum';
import { createToast, toastType } from 'Common/ToastHelper';
import { CLOSE_PRODUCTION_FORM_NAME } from 'Common/FormEnum';
import { LoadingBase } from 'Components/';
import { withReportSubscription } from 'State/report/withReportSubscription';
import { PrintProductionRecordFieldEnum as PrintProductionFields } from 'Common/FieldEnum';
import * as DialogActions from 'Components/dialogs/state/actions';
import { isPreferenceDisabled } from 'Common/Security/SecurityHelpers';
import { PreferencesEnum } from 'Common/Security/PreferencesEnum';

import * as ManageProductionSelectors from '../state/manageproduction/selectors';
import * as ManageProductionActions from '../state/manageproduction/actions';
import { ProductionRecordTableForm } from './ProductionRecordTableForm';
import { CloseProductionModal } from '../subpages/editproduction/components/CloseProductionModal';
import * as ProdRecordHelpers from '../helpers/productionRecord';

class ProductionRecordTable extends Component {
  static propTypes = {
    reportFetching: PropTypes.bool,
    getReport: PropTypes.func,
  };

  state = {
    selectedProdRecord: null,
    showCloseModal: false,
  };

  get columns() {
    const { sitesMap } = this.props;

    return ProdRecordHelpers.createProdRecordTableColumns({
      sitesMap,
      showCog: true,
      menuOptions: this.menuOptions,
    });
  }

  onEditClick = row => {
    const { history } = this.props;
    const startDate = moment(this.props.startDate);
    const endDate = moment(this.props.endDate);

    if (endDate.isBefore(startDate)) {
      createToast(
        toastType.error,
        'Error',
        'Start date must be before the end date.'
      );
      return;
    }

    // TODO handle site groups
    const siteIds = [row[ProductionGridFields.SiteId]];
    const queryParams = {
      [ProductionGridFields.ServingDT]: moment(
        row[ProductionGridFields.ServingDT]
      ).format(DATE_FORMAT_FOR_UI_DASHED),
      [EditProductionRecordFields.SiteIds]: siteIds,
    };

    const queryString = qs.stringify(queryParams);

    history.push(
      `/manageproduction/edit/${
        row[ProductionGridFields.CycleMenuId]
      }/${startDate.format(DATE_FORMAT_FOR_UI_DASHED)}/${endDate.format(
        DATE_FORMAT_FOR_UI_DASHED
      )}?${queryString}`
    );
  };

  onPrintClick = async row => {
    const { getReport } = this.props;
    const status = row[ProductionRecordFields.Status];
    const usePlannedNumbers = ProductionStatusEnum.Planning === status;
    const data = {
      [PrintProductionFields.FromDate]: row[ProductionRecordFields.ServingDT],
      [PrintProductionFields.ToDate]: row[ProductionRecordFields.ServingDT],
      [PrintProductionFields.SiteId]: row[ProductionRecordFields.SiteId],
      [PrintProductionFields.CycleMenuId]: row[ProductionRecordFields.CycleMenuId],
      [PrintProductionFields.UsePlannedNumbers]: usePlannedNumbers,
    };

    getReport('Production/Report', data);
  };

  get menuOptions() {
    const { isReadOnly } = this.props;

    return [
      {
        icon: 'pencil',
        text: isReadOnly ? 'View' : 'Edit',
        handler: this.onEditClick,
      },
      {
        icon: 'close',
        text: 'Close',
        handler: row => {
          this.openModal(row);
        },
        disabledWhen: row => {
          const id = row[ProductionRecordFields.Id];
          const isClosed = row[ProductionRecordFields.IsClosed];
          const status = row[ProductionGridFields.Status];

          return (
            _.isNil(id) ||
            Number(id) === 0 ||
            isClosed ||
            ProductionStatusEnum.Planning === status
          );
        },
      },
      {
        icon: 'print',
        text: 'Print',
        handler: row => this.onPrintClick(row),
      },
      {
        icon: 'folder open',
        text: 'Reopen',
        handler: row => this.showReopenWarning(row),
        disabledWhen: row => {
          const id = row[ProductionRecordFields.Id];
          const isClosed = row[ProductionRecordFields.IsClosed];
          const status = row[ProductionGridFields.Status];

          return (
            _.isNil(id) ||
            Number(id) === 0 ||
            !isClosed ||
            status !== ProductionStatusEnum.Closed ||
            isPreferenceDisabled(
              PreferencesEnum.ReopenProductRecord,
              this.props.security.preferences
            )
          );
        },
      },
    ];
  }

  openModal = prodRecord =>
    this.setState({ showCloseModal: true, selectedProdRecord: prodRecord });
  closeModal = () =>
    this.setState({ showCloseModal: false, selectedProdRecord: null });
  showReopenWarning = row =>
  {
    const { setNotificationDialog } = this.props;
    this.selectedRecordId = row.Id;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.REOPEN_PRODUCT_RECORD_CONFIRM,
      actionsToDispatchOnOk: [() => this.reopenProdRecord(row)],
    });
  };

  get modalProps() {
    const { showCloseModal } = this.state;
    return {
      open: showCloseModal,
      onClose: this.closeModal,
    };
  }

  get modalTitle() {
    const { sitesMap } = this.props;
    const { selectedProdRecord: prodRecord } = this.state;
    let title = '';

    if (prodRecord) {
      const site =
        _.get(sitesMap, [
          prodRecord[ProductionRecordFields.SiteId],
          'Description',
        ]) || prodRecord[ProductionRecordFields.SiteId];

      const date = moment(prodRecord[ProductionRecordFields.Date]).format(
        DATE_FORMAT_DAY_MONTH_NUMBER
      );

      const standard = prodRecord[ProductionRecordFields.Standard];

      title = `${site} - ${date} - ${standard}`;
    }
    return title;
  }

  reloadGrid = () => {
    const { getProdRecords,
      siteId,
      startDate,
      endDate,
      cycleMenuId,
      status,
    } = this.props;

    const data = {
      [ProductionGridFields.SiteIds]: [siteId],
      [ProductionGridFields.SiteId]: siteId,
      [ProductionGridFields.CycleMenuId]:  cycleMenuId,
      [ProductionGridFields.Status]: status,
      [ProductionGridFields.StartDate]: startDate,
      [ProductionGridFields.EndDate]: endDate,
    };

    getProdRecords(data);
  };

  closeProdRecord = async prodRecord => {
    const { closeProdRecord, reset } = this.props;
    const id = prodRecord[ProductionRecordFields.Id];
    await closeProdRecord(id);
    this.closeModal();
    reset(CLOSE_PRODUCTION_FORM_NAME);

    this.reloadGrid();
  };

  reopenProdRecord = async prodRecord => {
    const { reopenProdRecord } = this.props;
    const id = prodRecord[ProductionRecordFields.Id];
    await reopenProdRecord(id);

    this.reloadGrid();
  };

  render() {
    const {
      prodRecords,
      fetchingProdRecords,
      isReadOnly,
      fetchingCloseProd,
      reportFetching,
      siteId,
      startDate,
      endDate,
      cycleMenuId,
      status,
    } = this.props;

    const { selectedProdRecord } = this.state;

    if (reportFetching) {
      return <LoadingBase />;
    }

    return (
      <Fragment>
        <ProductionRecordTableForm
          initialValues={{
            [ProductionGridFields.StartDate]:
              startDate != null
                ? startDate
                : moment().format(DATE_FORMAT_FOR_UI),
            [ProductionGridFields.EndDate]:
              endDate != null
                ? endDate
                : moment()
                    .add(1, 'month')
                    .format(DATE_FORMAT_FOR_UI),
            [ProductionGridFields.SiteId]: siteId,
            [ProductionGridFields.CycleMenuId]: cycleMenuId,
            [ProductionGridFields.Status]: status,
          }}
        />
        <TitledCard title="Production Records">
          <ReactTable
            columns={this.columns}
            data={prodRecords}
            loading={fetchingProdRecords}
            minRows={5}
            showPageSizeOptions={false}
            defaultFilterMethod={caseInsensitiveFilter}
            defaultSorted={[
              {
                id: ProductionGridFields.CycleMenuDescription,
              },
            ]}
          />
        </TitledCard>
        <CloseProductionModal
          headerTitle={`Verify and Close Production Record - ${this.modalTitle}`}
          isReadOnly={isReadOnly}
          showComments={false}
          modalProps={this.modalProps}
          onSubmit={() => {
            this.closeProdRecord(selectedProdRecord);
          }}
          prodRecord={selectedProdRecord}
          loading={fetchingCloseProd}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  sitesMap: CommonStateSelectors.getSitesMap(state),
  prodRecords: ManageProductionSelectors.getProdRecords(state),
  fetchingProdRecords: ManageProductionSelectors.getFetchingProdRecords(state),
  startDate: ManageProductionSelectors.getStartDate(state),
  endDate: ManageProductionSelectors.getEndDate(state),
  fetchingCloseProd: ManageProductionSelectors.getFetchingCloseProdRecord(state),
  fetchingReopenProd: ManageProductionSelectors.getFetchingReopenProdRecord(state),
  siteId: ManageProductionSelectors.getSiteId(state),
  cycleMenuId: ManageProductionSelectors.getCycleMenuId(state),
  status: ManageProductionSelectors.getStatus(state),
  security: state.SecurityState,
});

const mapDispatchToProps = {
  closeProdRecord: ManageProductionActions.closeProdRecord,
  reopenProdRecord: ManageProductionActions.reopenProdRecord,
  setNotificationDialog: DialogActions.setNotificationDialog,
  reset,
  getProdRecords: ManageProductionActions.getProdRecords,
};

ProductionRecordTable = withRouter(
  withReportSubscription(
    connect(mapStateToProps, mapDispatchToProps)(ProductionRecordTable)
  )
);

export { ProductionRecordTable };



// WEBPACK FOOTER //
// ./src/pages/manageproduction/components/ProductionRecordTable.js